// Angular modules.
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
// Zenklub enums.
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
// Zenklub interfaces.
import {
	AppointmentSidebarFrom,
	ISidebar,
} from 'app/interfaces/sidebar.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { GlobalService } from 'app/services/global.service';
import { UserService } from 'app/services/user.service';
import { enterAnimation } from 'app/utils/animations.util';
import { DateTime } from 'luxon';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
import { ReplaySubject, Subject } from 'rxjs';
// Enum for clients pages.
enum EClientPages {
	Add,
	Details,
	Edit,
	List,
}
@Component({
	selector: 'app-clients',
	templateUrl: './clients.component.html',
	styleUrls: ['./clients.component.scss'],
	animations: [enterAnimation],
})
export class ClientsComponent implements OnInit {
	searchChanged: Subject<string> = new Subject<string>();
	// Active section.
	activeSection: EClientPages;
	pagesList: any = EClientPages;
	showFullHeader = false;
	// Sidebar data.
	activeSidebar: ISidebar;
	calendarLabels: any;
	sidebarVisible: string;
	modalVisible: ESidebarMode;
	// User data.
	userPref: IUserPreferences;
	// Mobile releated.
	isMobile = false;
	addAddress = '/clients/add';
	// Status.
	isLoading = false;
	// Refresh profile Completion
	public resetProfileCompletion: ReplaySubject<boolean> =
		new ReplaySubject<boolean>();
	public showHideDetails: ReplaySubject<boolean> = new ReplaySubject<boolean>();
	// Constructor.
	constructor(
		protected global: GlobalService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected translate: TranslateService,
		protected user: UserService,
		public sidebarService: AppointmentSidebarService
	) {
		this.resetProfileCompletion.next(true);
	}

	// On Init.
	public ngOnInit(): void {
		this.searchChanged = new Subject<string>();
		// Loader.
		this.isLoading = true;
		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();
		// Get active page.
		this.activeSection = this.getActivePage();
		// Get user preferences.
		this.userPref = this.user.getUserLocalPreferences();

		if (!this.isMobile) {
			// Active sidebar.
			this.activeSidebar = {
				date: new Date(),
				id: null,
				mode: ESidebarMode.Appointments,
			};
			this.changeActiveSidebar(this.activeSidebar);
			// Loader.
			this.isLoading = false;
		} else {
			// Loader.
			this.isLoading = false;
		}
	}

	// Get active section.
	public getActivePage(): EClientPages {
		const pathName: string = location.pathname;
		let currentView: EClientPages = EClientPages.List;
		if (pathName.indexOf('/clients/add') > -1) {
			currentView = EClientPages.Add;
			this.showFullHeader = false;
		} else if (pathName.indexOf('/clients/details') > -1) {
			currentView = EClientPages.Details;
			this.showFullHeader = true;
		} else if (pathName.indexOf('/clients/edit') > -1) {
			currentView = EClientPages.Edit;
			this.showFullHeader = false;
		} else {
			this.router.navigateByUrl('/contacts');
			currentView = EClientPages.List;
			this.showFullHeader = true;
		}
		return currentView;
	}

	// Add a new client.
	public addNewClient(): void {
		this.showFullHeader = false;
		this.router.navigate(['/clients/add']);
		this.activeSection = EClientPages.Add;
	}

	// Edit a client.
	public editClient(id: number): void {
		if (id) {
			this.showFullHeader = false;
			this.router.navigate(['/clients/edit/' + id]);
			this.activeSection = EClientPages.Edit;
		}
	}

	// Cancel add new client.
	public cancelAddClient(contactId): void {
		if (contactId) {
			this.router.navigateByUrl(`/clients/details/${contactId}`);
			this.activeSection = EClientPages.Details;
			return;
		}
		this.goToList();
	}

	// Go to list page.
	public goToList(): void {
		this.showFullHeader = true;
		this.router.navigateByUrl('/contacts');
	}

	// Show user's details.
	public showUserDetails(contactId: number): void {
		this.router.navigate([`/contact/${contactId}`]);
		this.activeSection = EClientPages.Details;
	}

	// Add an appointment.
	public newAppointment(data: any): void {
		if (data.userId) {
			// Active sidebar.
			this.activeSidebar = {
				date: new Date(),
				id: null,
				mode: ESidebarMode.New,
				contactId: data.userId,
				from: AppointmentSidebarFrom.OLD_USER_PROFILE,
			};
			this.changeActiveSidebar(this.activeSidebar);
		}
	}

	// Show details of an event.
	public showEventDetails(eventId: string): void {
		// Active sidebar.
		this.activeSidebar = {
			date: new Date(),
			id: eventId,
			mode: ESidebarMode.AppointmentDetails,
			contactId: null,
			scrollTo: true,
		};
		this.changeActiveSidebar(this.activeSidebar);
	}

	// Navigate.
	public goToPage(page: string): void {
		switch (page.toLowerCase()) {
			case '/':
			case 'calendar':
			case 'dashboard':
			case 'home':
				this.router.navigate(['/calendar']);
				break;
			case 'clients':
				this.router.navigate(['/clients']);
				break;
		}
	}

	// On window resize.
	public onResize(): void {
		// Update mobile info.
		this.isMobile = this.global.isMobileDevice();
	}

	/* RELEATED TO SIDEBAR.======================================================= */
	// Reschedule.
	public reschedule(sb: ISidebar): void {
		sb.mode = ESidebarMode.Edit;
		this.changeActiveSidebar(sb);
	}

	// Change sidebar content.
	protected async changeActiveSidebar(sidebar: ISidebar) {
		this.activeSidebar = {
			...sidebar,
			time: sidebar.hour
				? this.getTimeByString(sidebar.date, sidebar.hour)
				: null,
			id: sidebar.id ?? null,
			contactId: sidebar.contactId ?? null,
		};

		this.sidebarVisible = 'Appointments';
		this.modalVisible = sidebar.mode;

		switch (sidebar.mode) {
			case ESidebarMode.Edit:
			case ESidebarMode.Details:
				this.sidebarVisible = 'Details';
				break;
			default:
				this.sidebarVisible = 'Appointments';
		}
		this.showHideDetails.next(
			this.sidebarVisible !== 'Appointments' || !this.sidebarVisible
		);

		if (
			this.activeSidebar.mode === ESidebarMode.New ||
			this.activeSidebar.mode === ESidebarMode.Edit
		) {
			await this.sidebarService.setSidebar(true, this.activeSidebar);
		}
	}

	public getTimeByString(date, hour) {
		const _date = DateTime.fromJSDate(date);
		const _hour = DateTime.fromFormat(hour, 'HH:mm');

		_date.set({ hour: _hour.hour, minute: _hour.minute, second: 0 });
		return _date.toJSDate();
	}
}
